<template>
  <Slideover :visible="visible">
    <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
      <div class="flex items-start justify-between space-x-3">
        <h2 class="text-lg leading-7 font-medium">
          {{ title }}
        </h2>
        <div class="h-7 flex items-center">
          <button
            type="button"
            aria-label="Close panel"
            @click="$emit('toggle-slideover')"
            class="text-green-300 hover:text-white transition ease-in-out duration-150"
          >
            <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
    <div class="relative px-4 py-2" v-if="loadingAvailableInventories">
      <LoadingSpinner />
    </div>
    <div v-else class="px-4 py-2">
      <div class="space-y-2">
        <WarrantyClaimProduct_VBI
          v-for="(product, index) in warrantable"
          :key="index"
          :product="product"
          :shopify-line-item="getShopifyLineItem(product)"
          :warrantyDurationDays="warrantyDurationDays"
          v-on:add-to-processing="addToProcessing($event)"
          v-on:remove-from-processing="removeFromProcessing($event, true)"
          v-on:update-processing="updateProcessing($event)"
        />
      </div>

      <div v-if="mustCalculateRefund" class="flex items-center space-x-4 mt-2">
        <Button
          class="flex items-center space-x-2"
          @click-handler="loadCalculatedRefund()"
          :disabled="calculateDisabled"
        >
          <span>Calculate Value</span>
          <LoadingSpinner v-if="loadingCalculatedRefund" />
        </Button>
      </div>
      <div
        class="px-4 py-2 mt-2 border border-gray-300 rounded"
        v-if="showRefundValue"
      >
        <RefundValueSummary :calculated-refund="calculatedRefund" />
      </div>

      <div class="py-4 space-y-4">
        <!-- Reason -->
        <div>
          <label
            :for="`reason-${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Reason
          </label>
          <select
            :id="`reason-${_uid}`"
            v-model="reason"
            class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          >
            <option v-if="!reasonsLoaded" disabled selected value="">
              Loading...
            </option>
            <option v-else :value="null">Choose a Reason Code</option>
            <option v-for="(reason, index) in reasons" :key="index">
              {{ reason.Text1 }}
            </option>
          </select>
        </div>

        <!-- Note -->
        <div>
          <label
            :for="`note-${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Note
          </label>
          <div class="mt-1 rounded-md shadow-sm">
            <input
              :id="`note-${_uid}`"
              class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              v-model="note"
            />
          </div>
        </div>
      </div>

      <HttpError :error="error" dismissable />
      <no-longer-selling-alert
        v-if="!canShipAllDropships"
        :selected-for-processing="selectedForProcessing"
      />

      <!-- State restriction errors. -->
      <component
        v-if="hasRestrictedItems && !checkingStateRestrictions"
        :is="stateRestrictionComponent"
        :shipState="order.ShipState"
        :restrictedItems="restrictedItems"
      />

      <div v-if="!checkingStateRestrictions" class="pt-4 flex justify-end">
        <div class="relative inline-block text-left z-10">
          <div>
            <div class="flex items-center">
              <button
                class="bg-green-600 shadow-sm inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-l-md text-white transition duration-300 ease-in-out hover:bg-green-500 hover:shadow-outline-green disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="!canCreateReship"
                v-on:click="processWarranty('reship-charge-shipping')"
              >
                Reship and charge Customer
              </button>
              <button
                v-on:click="expandActions = !expandActions"
                :disabled="!(canIssueDiscount || canReshipWithoutCost)"
                class="bg-green-600 shadow-sm inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-r-md text-white transition duration-300 ease-in-out hover:bg-green-500 hover:shadow-outline-green disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <LoadingSpinner v-if="processing" />
                <icon v-else set="solid" name="chevron-down" class="h-5 w-5" />
              </button>
            </div>
          </div>
          <transition
            enter-active-class="ease-out duration-100"
            enter-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="ease-in duration-75"
            leave-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="expandActions && (canIssueDiscount || canReshipWithoutCost)"
              class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg"
            >
              <div class="rounded-md bg-white shadow-xs">
                <div
                  class="py-1"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="options-menu"
                >
                  <button
                    class="block w-full px-4 py-2 text-sm leading-5 text-gray-700 text-left hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-500 disabled:cursor-not-allowed"
                    role="menuitem"
                    v-on:click="processWarranty('issue-discount')"
                    :disabled="!canIssueDiscount"
                  >
                    Issue Discount Code
                  </button>
                  <button
                    class="block w-full px-4 py-2 text-sm leading-5 text-gray-700 text-left hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-500 disabled:cursor-not-allowed"
                    role="menuitem"
                    :disabled="!canReshipWithoutCost"
                    v-on:click="processWarranty('reship-no-charge')"
                  >
                    Reship without cost
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
      <LoadingSpinner v-else-if="checkingStateRestrictions" />
    </div>
  </Slideover>
</template>

<script>
import { ApiFactory } from "@/api/ApiFactory.js";
import Slideover from "@/components/layout/Slideover.vue";
import WarrantyClaimProduct_VBI from "./WarrantyClaimProduct.vue";
import RefundValueSummary from "@/views/Customers/components/slideovers/RefundValue/v2/RefundValueSummary";
import Button from "@/components/elements/Button.vue";
import stateRestrictionErrorVBI from "@/components/elements/VBI/StateRestrictionError.vue";
import claims from "@/mixins/claims";
import NoLongerSellingAlert from "@/components/elements/NoLongerSellingAlert";

const Order = ApiFactory.get("orders");
const Inventory = ApiFactory.get("inventories");
const Listing = ApiFactory.get("listings");

export default {
  name: "WarrantyClaim",
  components: {
    NoLongerSellingAlert,
    Slideover,
    WarrantyClaimProduct_VBI,
    RefundValueSummary,
    Button,
    stateRestrictionErrorVBI,
  },
  props: {
    title: {
      type: String,
      default: "Title",
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chargeShipping: true,
      selectedForProcessing: [],
      availableInventories: [],
      loadingAvailableInventories: false,
      expandActions: false,
      reason: null,
      reasons: [],
      reasonsLoaded: false,
      note: null,
      processing: false,
      error: null,
      checkingStateRestrictions: false,
      hasRestrictedItems: false,
      restrictedItems: [],
      mustCalculateRefund: true,
      calculatedRefund: null,
      loadingCalculatedRefund: false,
      shopifyDetails: null,
    };
  },
  mixins: [claims],
  computed: {
    stateRestrictionComponent() {
      return stateRestrictionErrorVBI;
    },
    calculateDisabled() {
      return !this.selectedItemsAreValid || this.loadingCalculatedRefund;
    },
    showRefundValue() {
      return !this.mustCalculateRefund && this.calculatedRefund !== null;
    },
    order() {
      return this.$store.getters["orders/get"]("order");
    },
    warrantyDurationDays() {
      if (!this.order.details) {
        return 0;
      }

      const warrantyDetail = this.order.details.find(
        (detail) =>
          detail.variant.product.subcategory == "Warranty" &&
          detail.variant.warranty_duration_days,
      );

      if (!warrantyDetail) {
        return 0;
      }

      return Number(warrantyDetail.variant.warranty_duration_days);
    },
    warrantable() {
      if (!this.order.details) {
        return [];
      }

      return this.order.details
        .filter((product) => {
          if (product.Adjustment != "0") {
            return false;
          }

          if (this.isOrderDetailCancelled(product)) {
            return false;
          }

          if (product.Type == "K") {
            return false;
          }

          if (
            ["HARDGOOD", "INTERNAL"].includes(
              product?.variant?.product?.type.toUpperCase(),
            )
          ) {
            return false;
          }

          return true;
        })
        .map((product) => {
          this.$set(
            product.variant,
            "inventory_counts",
            this.getProductAvailableInventory(product),
          );
          return product;
        });
    },
    canProcess() {
      if (this.mustCalculateRefund || this.calculatedRefund === null) {
        return false;
      }

      if (!this.inputIsValid) {
        return false;
      }

      if (this.processing) {
        return false;
      }

      return true;
    },
    canIssueDiscount() {
      return this.canProcess;
    },
    canCreateReship() {
      if (this.hasRestrictedItems) {
        return false;
      }

      if (!this.canShipAllDropships) {
        return false;
      }

      if (!this.canProcess) {
        return false;
      }

      if (this.selectedItemsAreShippable) {
        return false;
      }

      if (this.mustIssueDiscount) {
        return false;
      }

      return true;
    },
    canReshipWithoutCost() {
      return this.canCreateReship;
    },
    mustIssueDiscount() {
      let forcedDiscount = false;
      forcedDiscount = this.selectedForProcessing.filter(function (item) {
        return item.variant_id == item.replacement;
      }).length;

      return Boolean(forcedDiscount);
    },
    selectedItemsAreShippable() {
      if (this.selectedForProcessing.length == 0) {
        return false;
      }

      for (let selected of this.selectedForProcessing) {
        if (selected.isNotShippable) {
          return true;
        }
      }

      return false;
    },
    selectedItemsAreValid() {
      if (this.selectedForProcessing.length == 0) {
        return false;
      }

      for (let selected of this.selectedForProcessing) {
        if (!selected.isNotShippable && selected.quantity <= 0) {
          return false;
        }
        if (
          selected.quantity >
          selected.orderedQuantity - selected?.warrantyCount
        ) {
          return false;
        }
        if (selected.quantity > selected.orderedQuantity) {
          return false;
        }
      }

      return true;
    },
    inputIsValid() {
      if (!this.selectedItemsAreValid) {
        return false;
      }

      if (!this.reason) {
        return false;
      }

      if (!this.note) {
        return false;
      }

      return true;
    },
  },
  async created() {
    Listing.get({ list: "ADJ - Warranty Reasons" }).then((response) => {
      this.reasons = response.data.body;
      this.reasonsLoaded = true;
    });
    this.loadingAvailableInventories = true;
    await Order.shopifyDetails(this.order.SourceOrderNumber)
      .then((response) => {
        this.shopifyDetails = response.data.body;
      })
      .catch((e) => (this.error = e));
    await this.loadAvailableInventories();
  },
  methods: {
    getShopifyLineItem(product) {
      return this.shopifyDetails?.order?.lineItems?.edges?.find((edge) =>
        edge.node.id.endsWith(product.MarketLineID),
      )?.node;
    },
    loadCalculatedRefund() {
      this.loadingCalculatedRefund = true;
      Order.getCalculatedRefund(this.order.SourceOrderNumber, {
        products: this.selectedForProcessing,
      })
        .then((response) => {
          this.calculatedRefund = response.data.body;
          this.loadingCalculatedRefund = false;
          this.mustCalculateRefund = false;
        })
        .catch((e) => (this.error = e));
    },
    getCurrentRestrictedItems() {
      this.checkingStateRestrictions = true;
      this.restrictedItems = [];
      const selectedVariantIds = this.selectedForProcessing.map(
        (detail) => detail.variant_id,
      );
      Order.checkStateRestrictions(this.order.OrderNumber)
        .then((response) => {
          let res = response.data.body;
          for (const item of res.invalidItems) {
            if (selectedVariantIds.includes(item.variantId)) {
              this.restrictedItems.push(item.variantId);
            }
          }
          this.hasRestrictedItems = this.restrictedItems.length > 0;
          this.checkingStateRestrictions = false;
        })
        .catch((e) => (this.error = e));
    },
    isOrderDetailCancelled(detail) {
      return (
        detail?.Canceled === 1 || detail?.Product?.search(/^CANCELED/g) > -1
      );
    },
    processWarranty(action) {
      this.processing = true;
      this.error = null;

      Order.claimWarranty(this.order.SourceOrderNumber, {
        products: this.selectedForProcessing,
        action: action,
        reason: this.reason,
        comments: this.note,
        calculatedRefund: this.calculatedRefund,
      })
        .then(() => {
          this.$emit("toggle-slideover");
          this.reloadRelatedInformation();
        })
        .catch((error) => (this.error = error))
        .finally(() => (this.processing = false));
    },
    reloadRelatedInformation() {
      this.$store.dispatch("orders/refreshOrder", this.order.SourceOrderNumber);
    },
    loadAvailableInventories() {
      this.loadingAvailableInventories = true;

      Inventory.available({
        variant_ids: this.warrantable.map((item) => item.variant_id),
      })
        .then((response) => {
          if (Array.isArray(response.data.body)) {
            this.availableInventories = response.data.body;
          }
        })
        .finally(() => {
          this.loadingAvailableInventories = false;
        });
    },
    getProductAvailableInventory(product) {
      if (this.loadingAvailableInventories) {
        return "loading";
      }
      return this.availableInventories.find(
        (inventory) => inventory.id == product.variant_id,
      )?.inventory_counts;
    },
    resetCalculatedRefund() {
      this.mustCalculateRefund = true;
      this.calculatedRefund = null;
    },
    addToProcessing(detail) {
      this.resetCalculatedRefund();
      this.removeFromProcessing(detail, false);
      this.selectedForProcessing.push(detail);
      this.getCurrentRestrictedItems();
    },
    updateProcessing(detail) {
      this.resetCalculatedRefund();
      this.removeFromProcessing(detail);
      this.selectedForProcessing.push(detail);
    },
    removeFromProcessing(detail, stateRestrictionCheck) {
      this.resetCalculatedRefund();
      this.selectedForProcessing = this.selectedForProcessing.filter(
        (item) => item.itemNumber != detail.itemNumber,
      );
      if (this.selectedForProcessing?.length > 0 && stateRestrictionCheck) {
        this.getCurrentRestrictedItems();
      }
    },
  },
};
</script>
